import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  Radio,
  Typography,
} from '@mui/material';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Race } from '../../app/feathers/races/Race';
import { SearchableSelectInput } from '../SearchableSelectInput';
import { CandidatePhoto } from '../CandidatePhoto';
import { raceDistrictDisplayText } from '../../app/util/race-display';

type RaceOption = {
  _id: string;
  text: string;
  subText: string;
  race: Race;
};

type PrimaryCandidate = {
  id: string;
  name: string;
  photo?: string | null;
  party?: string;
  hasRunningMate: boolean;
};

type ConvertToRunningMateModalProps = {
  open: boolean;
  onClose: () => void;
  onConvert: (race: Race, primaryCandidateId: string) => Promise<void>;
  currentRace: Race;
};

const messageFromError = (error: unknown) => {
  if(
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  return 'Failed to convert candidate to running mate. Please try again.';
};

export const ConvertToRunningMateModal = ({
  open,
  onClose,
  onConvert,
  currentRace,
}: ConvertToRunningMateModalProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [raceOptions, setRaceOptions] = useState<RaceOption[]>([]);
  const [selectedRaceOption, setSelectedRaceOption] = useState<RaceOption | null>(null);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState<string | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if(open) {
      setRaceOptions([]);
      setSelectedRaceOption(null);
      setSelectedPrimaryId(null);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const searchRaces = async (searchText: string) => {
    if(!feathers) return;

    setOptionsLoading(true);
    try {
      const response = await feathers.getService('races').find({
        query: {
          election: currentRace.election,
          'runningMates.enabled': true,
          'runningMates.secondaryOffice': currentRace.office,
          _id: { $ne: currentRace._id },
          $limit: 25,
          ...(searchText
            ? {
              $or: [
                { candidatesSearchTerm: { $search: searchText } },
                { officeSearchTerm: { $search: searchText } },
              ],
            }
            : {}),
        },
      });
      setRaceOptions(response.data.map(race => ({
        _id: race._id,
        text: race.longName || race.officeName || race.raceKey,
        subText: raceDistrictDisplayText(race),
        race,
      })));
    } catch(searchError) {
      setError(messageFromError(searchError));
    } finally {
      setOptionsLoading(false);
    }
  };

  const primaryCandidates = useMemo<PrimaryCandidate[]>(() => {
    const selectedRace = selectedRaceOption?.race;
    if(!selectedRace) return [];

    const occupiedPrimaryIds = new Set(
      (selectedRace.runningMateSummary?.mainCandidateIds || []).map(String)
    );
    return (selectedRace.candidateSummary?.ids || []).map((candidateId, index) => ({
      id: String(candidateId),
      name: selectedRace.candidateSummary.names?.[index] || 'Unnamed candidate',
      photo: selectedRace.candidateSummary.photos?.[index],
      party: selectedRace.candidateSummary.parties?.[index],
      hasRunningMate: occupiedPrimaryIds.has(String(candidateId)),
    }));
  }, [selectedRaceOption]);

  const handleClose = () => {
    if(!submitting) onClose();
  };

  const handleConvert = async () => {
    if(!selectedRaceOption || !selectedPrimaryId) return;

    setSubmitting(true);
    setError(null);
    try {
      await onConvert(selectedRaceOption.race, selectedPrimaryId);
    } catch(convertError) {
      setError(messageFromError(convertError));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Convert to running mate</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Choose an eligible race in this election, then select the primary candidate this person will run with.
        </Typography>
        <SearchableSelectInput<RaceOption>
          name="race"
          onChangeSearchText={searchRaces}
          value={selectedRaceOption}
          onChange={({ target: { value } }) => {
            setSelectedRaceOption(value);
            setSelectedPrimaryId(null);
            setError(null);
          }}
          options={raceOptions}
          optionsLoading={optionsLoading}
          placeholder="Search for a race"
          disabled={submitting}
          style={{ width: '100%' }}
        />

        {selectedRaceOption && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Select a primary candidate
            </Typography>
            {primaryCandidates.length === 0 ? (
              <Alert severity="info">This race has no primary candidates.</Alert>
            ) : (
              <List disablePadding>
                {primaryCandidates.map(primaryCandidate => (
                  <ListItemButton
                    key={primaryCandidate.id}
                    selected={selectedPrimaryId === primaryCandidate.id}
                    disabled={primaryCandidate.hasRunningMate || submitting}
                    onClick={() => setSelectedPrimaryId(primaryCandidate.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <Radio
                      checked={selectedPrimaryId === primaryCandidate.id}
                      disabled={primaryCandidate.hasRunningMate || submitting}
                      tabIndex={-1}
                    />
                    <CandidatePhoto
                      src={primaryCandidate.photo}
                      className=""
                      style={{ width: 48, height: 48, flexShrink: 0 }}
                      onChange={() => {}}
                    />
                    <Box sx={{ ml: 1.5, flex: 1 }}>
                      <Typography>{primaryCandidate.name}</Typography>
                      {primaryCandidate.party && (
                        <Typography variant="body2" color="text.secondary">
                          {primaryCandidate.party}
                        </Typography>
                      )}
                    </Box>
                    {primaryCandidate.hasRunningMate && (
                      <Chip label="Running mate assigned" size="small" />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConvert}
          variant="contained"
          disabled={!selectedRaceOption || !selectedPrimaryId || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Converting...' : 'Convert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
